import React, { FC, useCallback } from 'react';

import { Box, HStack, Stack, UseDisclosureProps } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { find } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { getIcon, renderTime } from 'components/DataDisplay';
import { DetailsField } from 'components/DataDisplay/Utils/DetailsField';
import { CustomDayPicker } from 'components/DataEntry/Date';
import { IconTypes } from 'components/Icons';
import { renderSlaViolation } from 'containers/Findings/AlertDrawer/Components/Aggregated/utils';
import { AlertStatus } from 'containers/Findings/FindingsOverview/Components/AlertTables/Components/AlertStatus';
import { useEvidence } from 'containers/Visibility/SecurityGraphNext/Components/Evidence/hooks/useEvidence';
import { SidebarLink } from 'containers/Visibility/SecurityGraphNext/Components/Evidence/SidebarLink';
import { SidebarAccordion } from 'containers/Visibility/SecurityGraphNext/Components/SidebarAccordion';
import { selectSecurityGraphNext } from 'containers/Visibility/SecurityGraphNext/selectors';
import { actions } from 'containers/Visibility/SecurityGraphNext/slice';

interface RespondProps {
  remediate: UseDisclosureProps;
}

export const Respond: FC<RespondProps> = props => {
  const { remediate } = props;
  const dispatch = useDispatch();
  const { evidence } = useSelector(selectSecurityGraphNext);
  const { evidenceAction, alertStatus } = evidence;
  const { data: ticket } = alertStatus;
  const { getAlertStatus } = useEvidence();

  const alertStatusValue = alertStatus.data?.[0]?.ticket_status ?? 'To Do';

  const updateAlertDueDate = useCallback(
    date => {
      dispatch(
        actions.updateDueDate({
          q: {
            ticketUpdateRequest: {
              client_id: ticket?.[0]?.client_id,
              ticket_id: ticket?.[0]?.ticket_id!,
              field_type: 'due_date',
              field_value: date,
            },
          },
          onSuccess: () => {
            getAlertStatus();
          },
        }),
      );
    },
    [dispatch, ticket, getAlertStatus],
  );

  const renderTicket = () => {
    const jira =
      find(alertStatus.data, o => o.action === 'jira')?.ticket_id ?? undefined;
    const serviceNow =
      find(alertStatus.data, o => o.action === 'servicenow')?.ticket_id ??
      undefined;
    const renderItem = (item, label) => (
      <HStack>
        <Box boxSize={5}>{getIcon(label)}</Box>
        <Box>{item}</Box>
      </HStack>
    );
    return (
      <Stack>
        {jira && renderItem(jira, 'jira')}
        {serviceNow && renderItem(serviceNow, 'servicenow')}
        {!jira && !serviceNow && <Box>None</Box>}
      </Stack>
    );
  };

  return (
    <Stack spacing={3}>
      <SidebarAccordion header="RESPOND">
        <Stack spacing={3}>
          <SidebarLink
            iconType={IconTypes.Remediate}
            label="Remediation Options"
            tooltip={'Show auto-generated remediation commands'}
            onClick={() => {
              remediate?.onOpen?.();
            }}
          />

          <DetailsField
            label="Created at"
            value={renderTime({ value: ticket?.[0]?.created_at })}
          />
          <DetailsField
            label="Updated at"
            value={renderTime({ value: ticket?.[0]?.updated_at })}
          />
          <DetailsField
            label="Status"
            align="center"
            value={<AlertStatus status={alertStatusValue} />}
          />
          <DetailsField label="Ticket" value={renderTicket()} />
          <DetailsField
            label="Due date"
            align="center"
            value={
              <CustomDayPicker
                isLoading={evidenceAction.isLoading}
                dateProps={{
                  mode: 'single' as any,
                  disabled: { before: new Date() },
                  onSelect: (selected: any) => {
                    const day = dayjs(selected).format('YYYY-MM-DD');
                    updateAlertDueDate(day);
                  },
                  selected: (ticket?.[0]?.due_date as String)
                    ? (dayjs(ticket?.[0]?.due_date).utc().local() as any)
                    : undefined,
                }}
                portal
              />
            }
          />
          <DetailsField
            label="SLA violation"
            value={renderSlaViolation(ticket?.[0])}
          />
        </Stack>
      </SidebarAccordion>
    </Stack>
  );
};
